import { Plus, Search } from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router-dom'

import { useRecipes, useTags } from '@/api/hooks'
import { RecipeCard } from '@/components/RecipeCard'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import { fr } from '@/i18n/fr'

export function RecipeListPage() {
  const [searchInput, setSearchInput] = useState('')
  const [selectedTags, setSelectedTags] = useState<string[]>([])
  const search = useDebouncedValue(searchInput, 300)

  const { data: tags } = useTags()
  const { data: recipes, isPending } = useRecipes({ search, tags: selectedTags })

  function toggleTag(slug: string) {
    setSelectedTags((current) =>
      current.includes(slug) ? current.filter((value) => value !== slug) : [...current, slug],
    )
  }

  const hasFilters = search.length > 0 || selectedTags.length > 0

  return (
    <div>
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-2xl font-semibold tracking-tight">{fr.recipeList.title}</h1>
        <Button asChild size="sm">
          <Link to="/recipes/new">
            <Plus className="h-4 w-4" />
            <span className="hidden sm:inline">{fr.nav.newRecipe}</span>
          </Link>
        </Button>
      </div>

      <div className="relative mt-4">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
        <Input
          type="search"
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          placeholder={fr.recipeList.searchPlaceholder}
          className="pl-9"
        />
      </div>

      {tags && tags.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {tags.map((tag) => (
            <button key={tag.slug} type="button" onClick={() => toggleTag(tag.slug)}>
              <Badge variant={selectedTags.includes(tag.slug) ? 'selected' : 'outline'}>
                {tag.name} ({tag.recipe_count})
              </Badge>
            </button>
          ))}
          {hasFilters && (
            <button
              type="button"
              onClick={() => {
                setSearchInput('')
                setSelectedTags([])
              }}
              className="text-xs font-medium text-stone-500 underline"
            >
              {fr.recipeList.clearFilters}
            </button>
          )}
        </div>
      )}

      {isPending ? (
        <p className="mt-8 text-stone-500">{fr.common.loading}</p>
      ) : recipes && recipes.results.length > 0 ? (
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {recipes.results.map((recipe) => (
            <RecipeCard key={recipe.id} recipe={recipe} />
          ))}
        </div>
      ) : (
        <p className="mt-8 text-stone-500">
          {hasFilters ? fr.recipeList.emptyFiltered : fr.recipeList.empty}
        </p>
      )}
    </div>
  )
}
